import { motion } from "framer-motion";
import { Heart, Calendar, MapPin, Star, ArrowRight, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const Story = () => {
  const milestones = [
    {
      date: "First Meeting",
      description: "The day our eyes met and hearts connected forever",
      icon: <Star className="w-6 h-6" />,
      color: "from-yellow-400 to-orange-400"
    },
    {
      date: "First Date",
      description: "Coffee turned into endless conversations and laughter",
      icon: <Heart className="w-6 h-6" />,
      color: "from-rose-400 to-pink-400"
    },
    {
      date: "Our First Trip",
      description: "Exploring new places and creating memories together",
      icon: <MapPin className="w-6 h-6" />,
      color: "from-blue-400 to-purple-400"
    },
    {
      date: "Special Moments",
      description: "Every day with you feels like a beautiful dream",
      icon: <Calendar className="w-6 h-6" />,
      color: "from-green-400 to-teal-400"
    }
  ];

  return (
    <section className="py-20 px-6 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-16"
      >
        <h1 className="text-5xl md:text-6xl font-dancing text-rose-600 mb-4">
          Our Love Story 💕
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          A beautiful journey of love, laughter, and unforgettable moments
        </p>
      </motion.div>

      <div className="max-w-4xl mx-auto">
        {milestones.map((milestone, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.2 }}
            className={`flex ${
              index % 2 === 0 ? "flex-row" : "flex-row-reverse"
            } items-center mb-16`}
          >
            <div className="flex-1">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className={`bg-gradient-to-r ${milestone.color} p-8 rounded-3xl shadow-2xl text-white`}
              >
                <div className="flex items-center mb-4">
                  {milestone.icon}
                  <h3 className="text-2xl font-bold ml-3">{milestone.date}</h3>
                </div>
                <p className="text-lg opacity-90">{milestone.description}</p>
              </motion.div>
            </div>
            
            <div className="w-16 flex justify-center relative">
              <div className="w-4 h-4 bg-rose-500 rounded-full" />
              {index !== milestones.length - 1 && (
                <div className="absolute top-4 w-1 h-32 bg-gradient-to-b from-rose-400 to-pink-400" />
              )}
            </div>
            
            <div className="flex-1">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-white p-6 rounded-2xl shadow-lg border border-rose-100"
              >
                <div className="text-6xl mb-4 text-center">💫</div>
                <p className="text-gray-600 text-center">
                  {index === 0 && "The beginning of something magical..."}
                  {index === 1 && "Where our hearts truly connected..."}
                  {index === 2 && "Adventures that brought us closer..."}
                  {index === 3 && "Building our forever together..."}
                </p>
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="text-center mt-16"
      >
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 text-white py-8 px-6 rounded-3xl shadow-2xl max-w-2xl mx-auto">
          <h2 className="text-3xl font-dancing mb-4">And the story continues...</h2>
          <p className="text-xl">
            Every day with you is a new chapter in our beautiful love story. 
            Here's to many more adventures together! 💞
          </p>
        </div>
      </motion.div>

      {/* Navigation Button */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="flex justify-center mt-12"
      >
        <Link
          to="/gallery"
          className="flex items-center gap-2 bg-pink-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-pink-700 transition shadow-lg text-lg"
        >
          Next
          <ArrowRight className="w-6 h-6" />
        </Link>
      </motion.div>
    </section>
  );
};

export default Story;
